/**
 * Util compartido para el gráfico Type Visits del cash closing.
 *
 * COLLAPSE_INS / COLLAPSE_IV: flags de producto (criterio Marsel).
 * - COLLAPSE_INS=true → INS-* se suman a N/P, N/C, F/U, R/S
 * - COLLAPSE_IV=false → IV/NP e IV/NC aparecen como categorías propias
 * - Membresía: solo M/B (no sumar memberships.NP/NC encima)
 */

export const COLLAPSE_INS = true
export const COLLAPSE_IV = false

const BASE_LABELS = ['F/U', 'N/C', 'N/P', 'P/I', 'P/P', 'R/S', 'P/S', 'V/C', 'M/B']
const IV_LABELS = ['IV/NP', 'IV/NC']
const INS_LABELS = ['INS-NP', 'INS-NC', 'INS-F/U', 'INS-R/S']
const NA_LABEL = 'N/A'

const INS_TO_BASE = {
  'INS-NP': 'N/P',
  'INS-NC': 'N/C',
  'INS-F/U': 'F/U',
  'INS-R/S': 'R/S',
}

/** Solo cuando COLLAPSE_IV=true: IV se pliega a N/P / N/C */
const IV_TO_BASE = {
  'IV/NP': 'N/P',
  'IV/NC': 'N/C',
}

export function getTypeVisitLabels(
  collapseIns = COLLAPSE_INS,
  collapseIv = COLLAPSE_IV
) {
  const labels = BASE_LABELS.slice()
  if (!collapseIv) {
    labels.push(...IV_LABELS)
  }
  if (!collapseIns) {
    labels.push(...INS_LABELS)
  }
  labels.push(NA_LABEL)
  return labels
}

/**
 * Resuelve el prefix de un movimiento al label del gráfico.
 * @param {Object} movement
 * @param {{ collapseIns?: boolean, collapseIv?: boolean }} [options]
 */
export function resolveTypeVisitPrefix(movement, options = {}) {
  const collapseIns =
    options.collapseIns != null ? options.collapseIns : COLLAPSE_INS
  const collapseIv =
    options.collapseIv != null ? options.collapseIv : COLLAPSE_IV

  if (!movement || !movement.type_visit) return NA_LABEL

  const prefix = movement.type_visit.prefix || ''
  if (!prefix) return NA_LABEL

  if (collapseIns && INS_TO_BASE[prefix]) {
    return INS_TO_BASE[prefix]
  }
  if (collapseIv && IV_TO_BASE[prefix]) {
    return IV_TO_BASE[prefix]
  }

  const labels = getTypeVisitLabels(collapseIns, collapseIv)
  if (labels.indexOf(prefix) !== -1) return prefix

  return NA_LABEL
}

/**
 * Construye series alineadas a labels a partir de movimientos del día.
 * @param {Array} movements
 * @param {{ collapseIns?: boolean, collapseIv?: boolean }} [options]
 */
export function buildTypeVisitSeries(movements, options = {}) {
  const collapseIns =
    options.collapseIns != null ? options.collapseIns : COLLAPSE_INS
  const collapseIv =
    options.collapseIv != null ? options.collapseIv : COLLAPSE_IV
  const labels = getTypeVisitLabels(collapseIns, collapseIv)
  const counts = Object.create(null)
  labels.forEach(label => {
    counts[label] = 0
  })

  ;(movements || []).forEach(movement => {
    const label = resolveTypeVisitPrefix(movement, { collapseIns, collapseIv })
    counts[label] = (counts[label] || 0) + 1
  })

  return labels.map(label => counts[label] || 0)
}

/**
 * Resume un cash closing para gráficos Type Visits y Type Payment.
 * @param {Object} cashClosing
 * @param {{ collapseIns?: boolean, collapseIv?: boolean }} [options]
 * @returns {{ typeVisits: { labels: string[], series: number[] }, typePayment: { labels: string[], series: number[] } }}
 */
export function summarizeCashClosingCharts(cashClosing, options = {}) {
  const movements =
    cashClosing && Array.isArray(cashClosing.movements)
      ? cashClosing.movements
      : []

  const collapseIns =
    options.collapseIns != null ? options.collapseIns : COLLAPSE_INS
  const collapseIv =
    options.collapseIv != null ? options.collapseIv : COLLAPSE_IV
  const labels = getTypeVisitLabels(collapseIns, collapseIv)
  const series = buildTypeVisitSeries(movements, { collapseIns, collapseIv })

  const paymentCounts = { Cash: 0, Zelle: 0, Card: 0 }
  movements.forEach(m => {
    const key = m && m.type_payment
    if (key && Object.prototype.hasOwnProperty.call(paymentCounts, key)) {
      paymentCounts[key] += 1
    }
  })

  return {
    typeVisits: { labels, series },
    typePayment: {
      labels: Object.keys(paymentCounts),
      series: Object.values(paymentCounts),
    },
  }
}
